import { useEffect, useRef } from 'react';
import { animate, stagger } from 'animejs';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { getGridStaggerDelay } from '../../lib/animations/config';
import { Radio, Bell, Shield, Clock } from 'lucide-react';

export function AlertGridSection() {
    const { ref, isInView } = useScrollAnimation<HTMLElement>({ threshold: 0.3 });
    const reducedMotion = useReducedMotion();
    const hasAnimated = useRef(false);

    const features = [
        { icon: Radio, text: 'Instant network communication' },
        { icon: Bell, text: 'Multi-channel alerts (SMS, Email, Push)' },
        { icon: Shield, text: 'Redundant failsafe systems' },
        { icon: Clock, text: '< 5 second notification delivery' },
    ];

    useEffect(() => {
        if (!isInView || hasAnimated.current || reducedMotion) return;
        hasAnimated.current = true;

        // Animate grid dots from center outward
        animate('.alert-dot', {
            scale: [0, 1],
            opacity: [0, 1],
            duration: 600,
            ease: 'out(3)',
            delay: (_el: Element, i: number) => getGridStaggerDelay(i, 3, 80),
        });

        // Animate text content
        animate('.alert-heading', {
            translateX: [100, 0],
            opacity: [0, 1],
            duration: 800,
            ease: 'outExpo',
            delay: 200,
        });

        animate('.alert-text', {
            translateX: [50, 0],
            opacity: [0, 1],
            duration: 600,
            ease: 'outExpo',
            delay: 400,
        });

        animate('.alert-feature', {
            translateX: [50, 0],
            opacity: [0, 1],
            duration: 500,
            ease: 'outExpo',
            delay: stagger(100, { start: 500 }),
        });

        // Continuous center pulse
        animate('.center-dot-pulse', {
            scale: [1, 1.2],
            opacity: [1, 0.5],
            duration: 1500,
            ease: 'inOutSine',
            direction: 'alternate',
            loop: true,
        });
    }, [isInView, reducedMotion]);

    return (
        <section
            ref={ref}
            className="py-24 px-4 md:px-8 lg:px-16"
            style={{ background: 'var(--dark-bg)' }}
            aria-labelledby="alert-title"
        >
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Alert Grid Visual - Left */}
                    <div className="relative flex items-center justify-center py-12">
                        <div className="grid grid-cols-3 gap-8">
                            {[...Array(9)].map((_, index) => (
                                <div
                                    key={index}
                                    className={`alert-dot w-16 h-16 rounded-full flex items-center justify-center ${index === 4 ? 'center-dot-pulse' : ''
                                        } ${reducedMotion ? '' : 'opacity-0 scale-0'}`}
                                    style={{
                                        background: index === 4
                                            ? 'var(--gradient-fire)'
                                            : 'linear-gradient(135deg, #2a2a2a, #1e1e1e)',
                                        boxShadow: index === 4
                                            ? '0 0 30px var(--fire-orange)'
                                            : '0 4px 20px rgba(0, 0, 0, 0.3)',
                                    }}
                                >
                                    <div
                                        className={`w-4 h-4 rounded-full ${index === 4 ? 'bg-white' : 'bg-[var(--fire-orange)]'}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Text Content - Right */}
                    <div>
                        <h2
                            id="alert-title"
                            className={`alert-heading text-4xl md:text-5xl font-display font-bold text-white mb-6 ${reducedMotion ? '' : 'opacity-0'}`}
                        >
                            Connected{' '}
                            <span className="text-[var(--fire-orange)]">Alert Network</span>
                        </h2>

                        <p className={`alert-text text-gray-400 text-lg mb-8 ${reducedMotion ? '' : 'opacity-0'}`}>
                            Every sensor, every zone, every building - connected in real-time.
                            When danger is detected, everyone who needs to know is notified instantly.
                        </p>

                        <ul className="space-y-4" role="list">
                            {features.map((feature, index) => (
                                <li
                                    key={index}
                                    className={`alert-feature flex items-center gap-4 ${reducedMotion ? '' : 'opacity-0'}`}
                                >
                                    <div
                                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                                        style={{ background: 'rgba(255, 107, 53, 0.2)', border: '1px solid var(--fire-orange)' }}
                                    >
                                        <feature.icon className="w-5 h-5 text-[var(--fire-orange)]" aria-hidden="true" />
                                    </div>
                                    <span className="text-gray-300">{feature.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default AlertGridSection;
